/**
 * Where the session token lives.
 *
 * In the browser there is nothing better than localStorage. Inside the bundled
 * mobile apps there is: Capacitor Preferences is app-private storage rather than
 * a WebView origin's, so it is not reachable by injected script and it survives
 * WebView data clearing.
 *
 * Every access is wrapped: storage throws in private browsing, and a player
 * should get a sign-in screen rather than a blank page.
 */

const KEY = 'token';

type PreferencesApi = {
    get(options: { key: string }): Promise<{ value: string | null }>;
    set(options: { key: string; value: string }): Promise<void>;
    remove(options: { key: string }): Promise<void>;
};

function nativePreferences(): PreferencesApi | null {
    const capacitor = (globalThis as any).Capacitor;
    if (!capacitor?.isNativePlatform?.()) return null;
    return capacitor.Plugins?.Preferences ?? null;
}

export async function readToken(): Promise<string | null> {
    const preferences = nativePreferences();
    if (preferences) {
        try { return (await preferences.get({ key: KEY })).value; } catch { return null; }
    }
    try { return window.localStorage.getItem(KEY); } catch { return null; }
}

export async function writeToken(token: string): Promise<void> {
    const preferences = nativePreferences();
    if (preferences) {
        try { await preferences.set({ key: KEY, value: token }); return; } catch { return; }
    }
    try { window.localStorage.setItem(KEY, token); } catch { /* private mode: session only */ }
}

export async function clearToken(): Promise<void> {
    const preferences = nativePreferences();
    if (preferences) {
        try { await preferences.remove({ key: KEY }); return; } catch { return; }
    }
    try { window.localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

/**
 * True when the token lives in app-private native storage, which can only be
 * read asynchronously. The browser path stays synchronous so the first paint is
 * not delayed by a microtask hop.
 */
export function isNativeStorage(): boolean {
    return nativePreferences() !== null;
}

/** Synchronous read. Only meaningful when isNativeStorage() is false. */
export function readTokenSync(): string | null {
    if (nativePreferences()) return null;
    try { return window.localStorage.getItem(KEY); } catch { return null; }
}
